import { useCallback, useEffect, useState } from 'react'
import { Modal } from '@klear/ui/components/modal'
import { RadioGroup, RadioGroupItem } from '@klear/ui/components/radio-group'
import { Label } from '@klear/ui/components/label'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Badge } from '@klear/ui/components/badge'
import { KlearDate } from '@klear/date'
import { parseTimestamp } from '@klear/time/boundary'
import { trpc } from '@klear/ui/lib/trpc'
import { PERMISSION_ACTION_EDIT, ROUTE_ADMIN, ROUTE_CUSTOMERS } from '@klear/constants'
import { getAppBasePath } from '@klear/runtime-env'
import ExcelJS from 'exceljs'
import isDate from 'lodash/isDate'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import keys from 'lodash/keys'
import mapValues from 'lodash/mapValues'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'

const APP_BASE = getAppBasePath(ROUTE_ADMIN)

const COLUMN_TYPES: Record<string, { type: 'date' | 'number' | 'currency' | 'text' }> = {
  due_date: { type: 'date' },
  effective_date: { type: 'date' },
  trade_date: { type: 'date' },
  participation_trade_date: { type: 'date' },
  date_of_settlement: { type: 'date' },
  days_overdue: { type: 'number' },
  face_value: { type: 'currency' },
  purchase_price: { type: 'currency' },
  planned_repayment_ammount: { type: 'currency' },
  actual_repayment_amount: { type: 'currency' },
  maxium_coverage: { type: 'currency' }
}

function formatHeaderKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

function applyColumnFormats(sheet: ExcelJS.Worksheet, originalKeys: string[], headers: string[]) {
  const keyMap = new Map<string, string>()
  originalKeys.forEach((key) => {
    keyMap.set(formatHeaderKey(key), key)
  })

  for (let colIdx = 0; colIdx < headers.length; colIdx++) {
    const col = colIdx + 1
    const originalKey = keyMap.get(headers[colIdx]!)
    if (!originalKey) continue
    const ct = COLUMN_TYPES[originalKey]
    if (!ct || ct.type === 'text') continue

    const numFmt = ct.type === 'date' ? 'mm/dd/yyyy' : ct.type === 'currency' ? '$#,##0.00' : '#,##0'

    for (let row = 2; row <= sheet.rowCount; row++) {
      const cell = sheet.getRow(row).getCell(col)
      const v = cell.value
      if (v === '' || isNil(v)) continue

      if (ct.type === 'date') {
        const d = isString(v) || isDate(v) ? (parseTimestamp(v)?.toJsDate() ?? null) : null
        if (d && !isNaN(d.getTime())) {
          cell.value = d
          cell.numFmt = numFmt
        }
      } else if (ct.type === 'number') {
        const n = isString(v) ? parseFloat(v) : toNumber(v)
        if (!isNaN(n)) {
          cell.value = n
          cell.numFmt = numFmt
        }
      } else if (ct.type === 'currency') {
        const n = isString(v) ? parseFloat(toString(v).replace(/[^0-9.-]/g, '')) : toNumber(v)
        if (!isNaN(n)) {
          cell.value = n
          cell.numFmt = numFmt
        }
      }
    }
  }
}

function customerEditUrl(customerId: number): string {
  const path = `/${ROUTE_CUSTOMERS}/${customerId}/${PERMISSION_ACTION_EDIT}`
  return APP_BASE ? `${APP_BASE}${path}` : path
}

type DateRangeOption = 'current-month' | 'last-month' | 'all-time' | 'date-range'
type ModalStep = 'dates' | 'customers'

type CapitalReportExportModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  funderId: number
  funderOrganizationName: string
}

export function CapitalReportExportModal({
  open,
  onOpenChange,
  funderId,
  funderOrganizationName
}: CapitalReportExportModalProps) {
  const utils = trpc.useUtils()
  const [step, setStep] = useState<ModalStep>('dates')
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>('current-month')
  const [startDate, setStartDate] = useState<KlearDate | undefined>(undefined)
  const [endDate, setEndDate] = useState<KlearDate | undefined>(undefined)
  const [isAdvancing, setIsAdvancing] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [missingCustomers, setMissingCustomers] = useState<
    { customerId: number; name: string; role: 'buyer' | 'trader'; missingFields: string[] }[]
  >([])
  const [missingCount, setMissingCount] = useState(0)

  const resetModal = useCallback(() => {
    setStep('dates')
    setDateRangeOption('current-month')
    setStartDate(undefined)
    setEndDate(undefined)
    setIsAdvancing(false)
    setIsExporting(false)
    setMissingCustomers([])
    setMissingCount(0)
  }, [])

  useEffect(() => {
    if (!open) resetModal()
  }, [open, resetModal])

  const calculateDateRange = () => {
    const today = KlearDate.today()
    const lastMonth = today.subtract(1, 'month')
    switch (dateRangeOption) {
      case 'current-month':
        return {
          startDate: today.startOf('month'),
          endDate: today.endOf('month')
        }
      case 'last-month':
        return {
          startDate: lastMonth.startOf('month'),
          endDate: lastMonth.endOf('month')
        }
      case 'all-time':
        return {
          startDate: KlearDate.of('1900-01-01'),
          endDate: today.add(1, 'day')
        }
      case 'date-range':
        return {
          startDate,
          endDate
        }
    }
  }

  const datesInvalid = dateRangeOption === 'date-range' && (!startDate || !endDate)

  const handleAdvance = async () => {
    if (datesInvalid) return

    const { startDate: calculatedStartDate, endDate: calculatedEndDate } = calculateDateRange()
    if (!calculatedStartDate || !calculatedEndDate) return

    setIsAdvancing(true)
    try {
      const result = await utils.client.funder.capitalProviderReportMissingCustomers.query({
        funderId,
        startDate: calculatedStartDate.toJSON(),
        endDate: calculatedEndDate.toJSON()
      })
      setMissingCustomers(result.customers)
      setMissingCount(result.count)
      setStep('customers')
    } catch (error) {
      console.error('Failed to load missing customer data:', error)
    } finally {
      setIsAdvancing(false)
    }
  }

  const handleExport = async () => {
    if (datesInvalid) return

    setIsExporting(true)
    try {
      const { startDate: calculatedStartDate, endDate: calculatedEndDate } = calculateDateRange()
      if (!calculatedStartDate || !calculatedEndDate) {
        throw new Error('Start date and end date are required')
      }

      const data = await utils.client.funder.capitalProviderReport.query({
        funderId,
        startDate: calculatedStartDate.toJSON(),
        endDate: calculatedEndDate.toJSON()
      })

      const flattenData = (items: Record<string, unknown>[]): Record<string, string>[] => {
        return items.map((item) =>
          mapValues(item, (value) => {
            if (isNil(value)) return ''
            if (isObject(value)) return JSON.stringify(value)
            return toString(value)
          })
        )
      }

      const formatKeys = (items: Record<string, string>[]): Record<string, string>[] => {
        return items.map((item) => {
          const transformed: Record<string, string> = {}
          keys(item).forEach((key) => {
            transformed[formatHeaderKey(key)] = item[key] ?? ''
          })
          return transformed
        })
      }

      const workbook = new ExcelJS.Workbook()
      const sheets = [
        { name: 'Closed', data: data?.closed || [] },
        { name: 'Newly Funded', data: data?.newlyFunded || [] },
        { name: 'Current Outbound', data: data?.currentOutbound || [] },
        { name: '30 Days Late', data: data?.thirtyDaysLate || [] }
      ]

      for (const { name: sheetName, data: sheetData } of sheets) {
        const flattenedData = flattenData(sheetData as Record<string, unknown>[])
        const transformedData = formatKeys(flattenedData)
        const originalKeys = sheetData.length > 0 && sheetData[0] ? keys(sheetData[0]) : []
        const worksheet = workbook.addWorksheet(sheetName)

        if (transformedData.length === 0) {
          worksheet.addRow(['No rows in this period'])
        } else {
          const headers = keys(transformedData[0]!)
          worksheet.addRow(headers)
          for (const row of transformedData) {
            worksheet.addRow(headers.map((h) => row[h] ?? ''))
          }
          applyColumnFormats(worksheet, originalKeys, headers)
        }
      }

      const excelBuffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      const sanitizedName = funderOrganizationName.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      link.download = `capital-report-${sanitizedName}-${KlearDate.today().toJSON()}.xlsx`
      link.click()
      URL.revokeObjectURL(url)

      onOpenChange(false)
    } catch (error) {
      console.error('Failed to export capital report:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const isBusy = isAdvancing || isExporting

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Export Capital Report"
      description={
        step === 'dates'
          ? 'Select a date range for the capital report export.'
          : 'Review customers with missing data before exporting.'
      }
      primaryText={
        step === 'dates' ? (isAdvancing ? 'Checking...' : 'Next') : isExporting ? 'Exporting...' : 'Export Excel'
      }
      handlePrimaryAction={step === 'dates' ? () => void handleAdvance() : () => void handleExport()}
      primaryDisabled={step === 'dates' ? isBusy || datesInvalid : isExporting}
      secondaryText={step === 'dates' ? 'Cancel' : 'Back'}
      handleSecondaryAction={
        step === 'dates'
          ? () => onOpenChange(false)
          : () => {
              setStep('dates')
            }
      }
      secondaryDisabled={isBusy}
      size="md"
    >
      {step === 'dates' ? (
        <div className="space-y-4">
          <RadioGroup value={dateRangeOption} onValueChange={(value) => setDateRangeOption(value as DateRangeOption)}>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="current-month" id="current-month" />
                <Label htmlFor="current-month" className="cursor-pointer">
                  Current Month
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="last-month" id="last-month" />
                <Label htmlFor="last-month" className="cursor-pointer">
                  Last Month
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all-time" id="all-time" />
                <Label htmlFor="all-time" className="cursor-pointer">
                  All Time
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="date-range" id="date-range" />
                <Label htmlFor="date-range" className="cursor-pointer">
                  Date Range
                </Label>
              </div>
            </div>
          </RadioGroup>

          {dateRangeOption === 'date-range' && (
            <div className="space-y-4 px-3 py-3 pl-6">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <DatePicker value={startDate} onChange={setStartDate} />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <DatePicker value={endDate} onChange={setEndDate} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {missingCount === 0
              ? 'All customers in this report have the required data.'
              : `${missingCount} customer${missingCount === 1 ? '' : 's'} with missing data.`}
          </p>

          {missingCount > 0 ? (
            <div className="rounded-md border divide-y max-h-72 overflow-y-auto">
              {missingCustomers.map((customer) => (
                <button
                  key={`${customer.role}-${customer.customerId}`}
                  type="button"
                  className="w-full px-3 py-3 text-left hover:bg-muted/50 transition-colors"
                  onClick={() => window.open(customerEditUrl(customer.customerId), '_blank', 'noopener,noreferrer')}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium break-words">{customer.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Missing: {customer.missingFields.join(', ')}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 capitalize">
                      {customer.role}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          ) : null}

          {missingCount > 0 ? (
            <p className="text-xs text-muted-foreground">Click a customer to open their edit page in a new tab.</p>
          ) : null}
        </div>
      )}
    </Modal>
  )
}
